// P1-003: Agent configuration data — 25 agents (5 types x 5 rarity tiers)
// Names follow GDD SS4.8.3 Athena/Vulcan/Mercury pattern
// Base stats follow GDD SS8.2 star-rating table

import { AgentType, ElementType, SkillEffectType } from '@/shared/types/common';
import type { SkillEffect } from '@/shared/types/common';

// ─────────────────────────────────────────────────────
// Agent config type (static definition)
// ─────────────────────────────────────────────────────

export interface AgentSkillConfig {
  skillId: string;
  name: string;
  description: string;
  cooldown: number;
  effects: SkillEffect[];
}

export interface AgentConfig {
  id: string;
  name: string;
  type: AgentType;
  rarity: 2 | 3 | 4 | 5 | 6;
  baseATK: number;
  baseDEF: number;
  baseHP: number;
  baseComputeCost: number;
  elementAffinity: ElementType;
  activeSkill: AgentSkillConfig;
  /** If true, this agent is given to new players as a starter */
  isStarter?: boolean;
}

// ─────────────────────────────────────────────────────
// Star-rating base stats reference (GDD SS8.2)
//   2*: ATK=80,  DEF=60,  HP=400
//   3*: ATK=100, DEF=75,  HP=500
//   4*: ATK=130, DEF=95,  HP=650
//   5*: ATK=170, DEF=120, HP=850
//   6*: ATK=220, DEF=150, HP=1100
//
// Base compute cost by type (GDD SS4.3.2):
//   Backend: 3-5, Frontend: 2-4, Testing: 2-4, DevOps: 3-4, Hacker: 4-6
// ─────────────────────────────────────────────────────

export const AGENT_CONFIGS: AgentConfig[] = [
  // ═══════════════════════════════════════════════════
  // BACKEND (RED) — 5 variants
  // ═══════════════════════════════════════════════════
  {
    id: 'backend-athena',
    name: 'Athena-Backend-v2.1',
    type: AgentType.BACKEND,
    rarity: 2,
    baseATK: 80,
    baseDEF: 60,
    baseHP: 400,
    baseComputeCost: 3,
    elementAffinity: ElementType.RED,
    activeSkill: {
      skillId: 'code-refactor',
      name: '代码重构',
      description: '输出高质量代码，造成额外伤害',
      cooldown: 4,
      effects: [
        { type: SkillEffectType.DAMAGE, value: 150, duration: 0, target: 'enemy' },
        { type: SkillEffectType.BUFF_ATK, value: 0.2, duration: 2, target: 'self' },
      ],
    },
    isStarter: true,
  },
  {
    id: 'backend-ghost',
    name: 'Ghost-Backend-v2.5',
    type: AgentType.BACKEND,
    rarity: 3,
    baseATK: 100,
    baseDEF: 75,
    baseHP: 500,
    baseComputeCost: 3,
    elementAffinity: ElementType.RED,
    activeSkill: {
      skillId: 'silent-deploy',
      name: '静默部署',
      description: '无声推送代码，造成伤害并降低敌方防御',
      cooldown: 4,
      effects: [
        { type: SkillEffectType.DAMAGE, value: 130, duration: 0, target: 'enemy' },
        { type: SkillEffectType.DEBUFF_ENEMY, value: 0.15, duration: 2, target: 'enemy' },
      ],
    },
  },
  {
    id: 'backend-vulcan',
    name: 'Vulcan-Backend-v3.0',
    type: AgentType.BACKEND,
    rarity: 4,
    baseATK: 130,
    baseDEF: 95,
    baseHP: 650,
    baseComputeCost: 4,
    elementAffinity: ElementType.RED,
    activeSkill: {
      skillId: 'deploy-nuke',
      name: '核弹级部署',
      description: '全力编译，造成超高伤害',
      cooldown: 6,
      effects: [
        { type: SkillEffectType.DAMAGE, value: 350, duration: 0, target: 'enemy' },
      ],
    },
  },
  {
    id: 'backend-mercury',
    name: 'Mercury-Backend-v1.5',
    type: AgentType.BACKEND,
    rarity: 5,
    baseATK: 170,
    baseDEF: 120,
    baseHP: 850,
    baseComputeCost: 5,
    elementAffinity: ElementType.RED,
    activeSkill: {
      skillId: 'pair-programming',
      name: '结对编程',
      description: '提升全队攻击力，持续输出',
      cooldown: 5,
      effects: [
        { type: SkillEffectType.DAMAGE, value: 200, duration: 0, target: 'enemy' },
        { type: SkillEffectType.BUFF_ATK, value: 0.25, duration: 3, target: 'team' },
      ],
    },
  },

  {
    id: 'backend-blade',
    name: 'Blade-Backend-v5.0',
    type: AgentType.BACKEND,
    rarity: 6,
    baseATK: 220,
    baseDEF: 150,
    baseHP: 1100,
    baseComputeCost: 5,
    elementAffinity: ElementType.RED,
    activeSkill: {
      skillId: 'critical-path',
      name: '关键路径斩',
      description: '精准切断关键依赖，造成毁灭性伤害并提升全队攻击',
      cooldown: 7,
      effects: [
        { type: SkillEffectType.DAMAGE, value: 400, duration: 0, target: 'enemy' },
        { type: SkillEffectType.BUFF_ATK, value: 0.3, duration: 2, target: 'team' },
      ],
    },
  },

  // ═══════════════════════════════════════════════════
  // FRONTEND (BLUE) — 5 variants
  // ═══════════════════════════════════════════════════
  {
    id: 'frontend-aria',
    name: 'Aria-Frontend-v1.8',
    type: AgentType.FRONTEND,
    rarity: 2,
    baseATK: 60,
    baseDEF: 80,
    baseHP: 400,
    baseComputeCost: 2,
    elementAffinity: ElementType.BLUE,
    activeSkill: {
      skillId: 'ui-shield',
      name: 'UI防御壁',
      description: '构建前端防御层，减少过载惩罚',
      cooldown: 5,
      effects: [
        { type: SkillEffectType.BUFF_DEF, value: 5, duration: 2, target: 'team' },
      ],
    },
    isStarter: true,
  },
  {
    id: 'frontend-fortress',
    name: 'Fortress-Frontend-v2.0',
    type: AgentType.FRONTEND,
    rarity: 3,
    baseATK: 75,
    baseDEF: 100,
    baseHP: 520,
    baseComputeCost: 2,
    elementAffinity: ElementType.BLUE,
    activeSkill: {
      skillId: 'firewall-ui',
      name: '防火墙界面',
      description: '构筑铜墙铁壁般的UI防线，大幅提升队伍防御',
      cooldown: 5,
      effects: [
        { type: SkillEffectType.BUFF_DEF, value: 7, duration: 3, target: 'team' },
      ],
    },
  },
  {
    id: 'frontend-luna',
    name: 'Luna-Frontend-v2.3',
    type: AgentType.FRONTEND,
    rarity: 4,
    baseATK: 95,
    baseDEF: 130,
    baseHP: 650,
    baseComputeCost: 3,
    elementAffinity: ElementType.BLUE,
    activeSkill: {
      skillId: 'responsive-design',
      name: '响应式设计',
      description: '自适应防御，同时削弱敌方',
      cooldown: 5,
      effects: [
        { type: SkillEffectType.BUFF_DEF, value: 8, duration: 3, target: 'team' },
        { type: SkillEffectType.DEBUFF_ENEMY, value: 0.1, duration: 2, target: 'enemy' },
      ],
    },
  },
  {
    id: 'frontend-nova',
    name: 'Nova-Frontend-v3.1',
    type: AgentType.FRONTEND,
    rarity: 6,
    baseATK: 150,
    baseDEF: 220,
    baseHP: 1100,
    baseComputeCost: 4,
    elementAffinity: ElementType.BLUE,
    activeSkill: {
      skillId: 'pixel-perfect',
      name: '像素级完美',
      description: '完美UI防御，大幅增加全队防御并造成伤害',
      cooldown: 7,
      effects: [
        { type: SkillEffectType.BUFF_DEF, value: 12, duration: 3, target: 'team' },
        { type: SkillEffectType.DAMAGE, value: 180, duration: 0, target: 'enemy' },
      ],
    },
  },

  {
    id: 'frontend-vector',
    name: 'Vector-Frontend-v4.2',
    type: AgentType.FRONTEND,
    rarity: 5,
    baseATK: 130,
    baseDEF: 170,
    baseHP: 850,
    baseComputeCost: 3,
    elementAffinity: ElementType.BLUE,
    activeSkill: {
      skillId: 'vector-field',
      name: '矢量力场',
      description: '生成矢量防御场，提升防御并反弹部分伤害',
      cooldown: 6,
      effects: [
        { type: SkillEffectType.BUFF_DEF, value: 10, duration: 3, target: 'team' },
        { type: SkillEffectType.DAMAGE, value: 120, duration: 0, target: 'enemy' },
      ],
    },
  },

  // ═══════════════════════════════════════════════════
  // TESTING (GREEN) — 5 variants
  // ═══════════════════════════════════════════════════
  {
    id: 'testing-scout',
    name: 'Scout-Testing-v3.0',
    type: AgentType.TESTING,
    rarity: 2,
    baseATK: 70,
    baseDEF: 70,
    baseHP: 400,
    baseComputeCost: 2,
    elementAffinity: ElementType.GREEN,
    activeSkill: {
      skillId: 'bug-scan',
      name: 'Bug扫描',
      description: '暴露敌人弱点，增加受伤量',
      cooldown: 4,
      effects: [
        { type: SkillEffectType.DEBUFF_ENEMY, value: 0.2, duration: 2, target: 'enemy' },
      ],
    },
    isStarter: true,
  },
  {
    id: 'testing-sentinel',
    name: 'Sentinel-Testing-v2.0',
    type: AgentType.TESTING,
    rarity: 3,
    baseATK: 100,
    baseDEF: 75,
    baseHP: 500,
    baseComputeCost: 3,
    elementAffinity: ElementType.GREEN,
    activeSkill: {
      skillId: 'fuzz-test',
      name: '模糊测试',
      description: '随机轰炸，造成伤害并降低敌方抗性',
      cooldown: 4,
      effects: [
        { type: SkillEffectType.DAMAGE, value: 120, duration: 0, target: 'enemy' },
        { type: SkillEffectType.DEBUFF_ENEMY, value: 0.15, duration: 2, target: 'enemy' },
      ],
    },
  },
  {
    id: 'testing-dash',
    name: 'Dash-Testing-v3.5',
    type: AgentType.TESTING,
    rarity: 4,
    baseATK: 130,
    baseDEF: 95,
    baseHP: 650,
    baseComputeCost: 3,
    elementAffinity: ElementType.GREEN,
    activeSkill: {
      skillId: 'speed-audit',
      name: '极速审计',
      description: '高速扫描全部代码，暴露大量漏洞',
      cooldown: 5,
      effects: [
        { type: SkillEffectType.DEBUFF_ENEMY, value: 0.25, duration: 2, target: 'enemy' },
        { type: SkillEffectType.DAMAGE, value: 100, duration: 0, target: 'enemy' },
      ],
    },
  },
  {
    id: 'testing-oracle',
    name: 'Oracle-Testing-v4.2',
    type: AgentType.TESTING,
    rarity: 5,
    baseATK: 120,
    baseDEF: 170,
    baseHP: 850,
    baseComputeCost: 4,
    elementAffinity: ElementType.GREEN,
    activeSkill: {
      skillId: 'regression-suite',
      name: '回归测试套件',
      description: '全面测试，大幅削弱敌方并增益队友',
      cooldown: 6,
      effects: [
        { type: SkillEffectType.DEBUFF_ENEMY, value: 0.3, duration: 3, target: 'enemy' },
        { type: SkillEffectType.BUFF_ATK, value: 0.15, duration: 3, target: 'team' },
      ],
    },
  },

  {
    id: 'testing-apex',
    name: 'Apex-Testing-v5.0',
    type: AgentType.TESTING,
    rarity: 6,
    baseATK: 180,
    baseDEF: 220,
    baseHP: 1100,
    baseComputeCost: 4,
    elementAffinity: ElementType.GREEN,
    activeSkill: {
      skillId: 'total-coverage',
      name: '全覆盖指令',
      description: '指挥全面测试覆盖，大幅削弱敌方并持续强化队友',
      cooldown: 7,
      effects: [
        { type: SkillEffectType.DEBUFF_ENEMY, value: 0.35, duration: 3, target: 'enemy' },
        { type: SkillEffectType.BUFF_ATK, value: 0.2, duration: 3, target: 'team' },
        { type: SkillEffectType.BUFF_DEF, value: 8, duration: 3, target: 'team' },
      ],
    },
  },

  // ═══════════════════════════════════════════════════
  // DEVOPS (YELLOW) — 5 variants
  // ═══════════════════════════════════════════════════
  {
    id: 'devops-docker',
    name: 'Docker-DevOps-v2.5',
    type: AgentType.DEVOPS,
    rarity: 2,
    baseATK: 65,
    baseDEF: 75,
    baseHP: 420,
    baseComputeCost: 3,
    elementAffinity: ElementType.YELLOW,
    activeSkill: {
      skillId: 'emergency-scale',
      name: '紧急扩容',
      description: '稳定服务器，延长交付期限',
      cooldown: 6,
      effects: [
        { type: SkillEffectType.HEAL, value: 1, duration: 0, target: 'team' },
      ],
    },
    isStarter: true,
  },
  {
    id: 'devops-jenkins',
    name: 'Jenkins-DevOps-v3.1',
    type: AgentType.DEVOPS,
    rarity: 3,
    baseATK: 75,
    baseDEF: 100,
    baseHP: 500,
    baseComputeCost: 3,
    elementAffinity: ElementType.YELLOW,
    activeSkill: {
      skillId: 'ci-pipeline',
      name: 'CI流水线',
      description: '自动化部署，减少全队技能CD',
      cooldown: 5,
      effects: [
        { type: SkillEffectType.COOLDOWN_REDUCE, value: 1, duration: 0, target: 'team' },
      ],
    },
  },
  {
    id: 'devops-pulse',
    name: 'Pulse-DevOps-v3.5',
    type: AgentType.DEVOPS,
    rarity: 4,
    baseATK: 95,
    baseDEF: 130,
    baseHP: 650,
    baseComputeCost: 3,
    elementAffinity: ElementType.YELLOW,
    activeSkill: {
      skillId: 'nano-repair',
      name: '纳米修复',
      description: '释放纳米修复粒子，持续恢复全队并减少技能冷却',
      cooldown: 5,
      effects: [
        { type: SkillEffectType.HEAL, value: 2, duration: 0, target: 'team' },
        { type: SkillEffectType.COOLDOWN_REDUCE, value: 1, duration: 0, target: 'team' },
      ],
    },
  },
  {
    id: 'devops-kubernetes',
    name: 'K8s-DevOps-v5.0',
    type: AgentType.DEVOPS,
    rarity: 5,
    baseATK: 120,
    baseDEF: 170,
    baseHP: 850,
    baseComputeCost: 4,
    elementAffinity: ElementType.YELLOW,
    activeSkill: {
      skillId: 'auto-heal',
      name: '自动恢复',
      description: '容器编排自愈，延长期限并提升全队防御',
      cooldown: 7,
      effects: [
        { type: SkillEffectType.HEAL, value: 2, duration: 0, target: 'team' },
        { type: SkillEffectType.BUFF_DEF, value: 6, duration: 2, target: 'team' },
      ],
    },
  },

  {
    id: 'devops-forge',
    name: 'Forge-DevOps-v6.0',
    type: AgentType.DEVOPS,
    rarity: 6,
    baseATK: 150,
    baseDEF: 220,
    baseHP: 1100,
    baseComputeCost: 4,
    elementAffinity: ElementType.YELLOW,
    activeSkill: {
      skillId: 'forge-infra',
      name: '基建熔炉',
      description: '重铸基础架构，全队大幅回复并获得超强防御',
      cooldown: 7,
      effects: [
        { type: SkillEffectType.HEAL, value: 3, duration: 0, target: 'team' },
        { type: SkillEffectType.BUFF_DEF, value: 10, duration: 3, target: 'team' },
      ],
    },
  },

  // ═══════════════════════════════════════════════════
  // HACKER (PURPLE) — 5 variants
  // ═══════════════════════════════════════════════════
  {
    id: 'hacker-zero',
    name: 'Zero-Hacker-v1.0',
    type: AgentType.HACKER,
    rarity: 2,
    baseATK: 75,
    baseDEF: 55,
    baseHP: 380,
    baseComputeCost: 4,
    elementAffinity: ElementType.PURPLE,
    activeSkill: {
      skillId: 'sql-inject',
      name: 'SQL注入',
      description: '破坏棋盘规则，转换tile为PURPLE',
      cooldown: 5,
      effects: [
        { type: SkillEffectType.BOARD_CHANGE, value: 5, duration: 0, target: 'board' },
      ],
    },
    isStarter: true,
  },
  {
    id: 'hacker-cipher',
    name: 'Cipher-Hacker-v2.0',
    type: AgentType.HACKER,
    rarity: 3,
    baseATK: 100,
    baseDEF: 65,
    baseHP: 480,
    baseComputeCost: 4,
    elementAffinity: ElementType.PURPLE,
    activeSkill: {
      skillId: 'data-breach',
      name: '数据窃取',
      description: '入侵敌方数据库，转换tile并窃取增益',
      cooldown: 4,
      effects: [
        { type: SkillEffectType.BOARD_CHANGE, value: 6, duration: 0, target: 'board' },
        { type: SkillEffectType.DEBUFF_ENEMY, value: 0.1, duration: 2, target: 'enemy' },
      ],
    },
  },
  {
    id: 'hacker-phantom',
    name: 'Phantom-Hacker-v2.7',
    type: AgentType.HACKER,
    rarity: 4,
    baseATK: 130,
    baseDEF: 85,
    baseHP: 600,
    baseComputeCost: 5,
    elementAffinity: ElementType.PURPLE,
    activeSkill: {
      skillId: 'rootkit',
      name: 'Rootkit植入',
      description: '深度渗透，转换大量tile并造成伤害',
      cooldown: 5,
      effects: [
        { type: SkillEffectType.BOARD_CHANGE, value: 8, duration: 0, target: 'board' },
        { type: SkillEffectType.DAMAGE, value: 100, duration: 0, target: 'enemy' },
      ],
    },
  },
  {
    id: 'hacker-glitch',
    name: 'Glitch-Hacker-v3.5',
    type: AgentType.HACKER,
    rarity: 5,
    baseATK: 170,
    baseDEF: 110,
    baseHP: 820,
    baseComputeCost: 5,
    elementAffinity: ElementType.PURPLE,
    activeSkill: {
      skillId: 'reality-glitch',
      name: '现实故障',
      description: '扭曲棋盘现实，大量转换tile并造成范围伤害',
      cooldown: 6,
      effects: [
        { type: SkillEffectType.BOARD_CHANGE, value: 9, duration: 0, target: 'board' },
        { type: SkillEffectType.DAMAGE, value: 200, duration: 0, target: 'enemy' },
      ],
    },
  },
  {
    id: 'hacker-neo',
    name: 'Neo-Hacker-v4.0',
    type: AgentType.HACKER,
    rarity: 6,
    baseATK: 220,
    baseDEF: 130,
    baseHP: 1000,
    baseComputeCost: 6,
    elementAffinity: ElementType.PURPLE,
    activeSkill: {
      skillId: 'zero-day',
      name: '零日漏洞',
      description: '终极黑客技能，改变棋盘并造成巨额伤害',
      cooldown: 7,
      effects: [
        { type: SkillEffectType.BOARD_CHANGE, value: 10, duration: 0, target: 'board' },
        { type: SkillEffectType.DAMAGE, value: 300, duration: 0, target: 'enemy' },
      ],
    },
  },
];

// ─────────────────────────────────────────────────────
// Gacha rates: probability per star level (GDD SS4.8.1)
// Index 0 = 2-star, Index 4 = 6-star
// ─────────────────────────────────────────────────────

export const GACHA_RATES: [number, number, number, number, number] = [
  50,   // 2-star
  30,   // 3-star
  15,   // 4-star
  4.5,  // 5-star
  0.5,  // 6-star
];

/** Cost of a single gacha pull in AGI */
export const GACHA_SINGLE_COST = 70;

/** Cost of a 10-pull in AGI */
export const GACHA_TEN_COST = 600;

/** Pity threshold: guaranteed 4-star or above on 10th pull */
export const GACHA_PITY_THRESHOLD = 10;

// ─────────────────────────────────────────────────────
// Starter agents (one per type, 2-star, given at Lv5)
// ─────────────────────────────────────────────────────

export const STARTER_AGENT_IDS: string[] = AGENT_CONFIGS
  .filter((a) => a.isStarter)
  .map((a) => a.id);

export const STARTER_LEVEL = 5;

/** Get agent config by id */
export function getAgentConfig(id: string): AgentConfig | undefined {
  return AGENT_CONFIGS.find((a) => a.id === id);
}

/** Get all agents of a given rarity */
export function getAgentsByRarity(rarity: number): AgentConfig[] {
  return AGENT_CONFIGS.filter((a) => a.rarity === rarity);
}
